/**
 * useTaskKanbanCounts - Contadores por fase do Kanban de Tarefas
 * 
 * Busca total e atrasadas por fase via RPC get_task_kanban_column_counts.
 * Segue padrão do useKanbanConversationPhaseStats.
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { TaskKanbanFilters } from './useTaskKanban';

// Logger para depuração
const logger = createLogger('TaskKanbanCounts');

/**
 * Dados de contagem de uma fase
 */
export interface TaskPhaseCount {
  phase_id: string;
  phase_name: string;
  phase_color: string;
  phase_sort_order: number;
  phase_is_final: boolean;
  total_count: number;
  overdue_count: number;
}

/**
 * Hook para buscar contagens por fase de uma categoria
 * 
 * @param categoryId - ID da categoria
 * @param filters - Filtros ativos
 */
export function useTaskKanbanCounts(
  categoryId: string | null,
  filters?: TaskKanbanFilters
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['task-kanban-counts', categoryId, filters],
    queryFn: async () => {
      logger.debug('Buscando contagens do Kanban de Tarefas', { categoryId });

      const { data, error } = await supabase.rpc('get_task_kanban_column_counts', {
        p_category_id: categoryId!,
        p_status_filter: (filters?.status as any) || null,
        p_priority_filter: (filters?.priority as any) || null,
        p_assigned_to_filter: filters?.assigned_to || null,
        p_search: filters?.search || null,
        // v1.8.0: Novos filtros avançados
        p_classification_id: filters?.classification_id || null,
        p_created_by_filter: filters?.created_by || null,
        p_due_date_from: filters?.due_date_from || null,
        p_due_date_to: filters?.due_date_to || null,
        p_created_from: filters?.created_from || null,
        p_created_to: filters?.created_to || null,
        p_completed_from: filters?.completed_from || null,
        p_completed_to: filters?.completed_to || null,
        p_tenant_hint: currentTenant?.id || null,
        // v1.1.0: Filtro "Sem Prazo" / "Com Prazo"
        p_due_date_is_null: filters?.due_date_is_null ?? null,
        // v1.5.0: filtros exclusivos de Retenção/Jurídico
        p_request_from: filters?.request_from || null,
        p_request_to: filters?.request_to || null,
        p_period: filters?.period || null,
        p_legal_deadline_from: filters?.legal_deadline_from || null,
        p_legal_deadline_to: filters?.legal_deadline_to || null,
      } as any);

      if (error) {
        logger.error('Erro ao buscar contagens do Kanban de Tarefas:', error);
        throw error;
      }

      return (data || []) as TaskPhaseCount[];
    },
    enabled: !!categoryId && !!currentTenant?.id,
    staleTime: 15 * 1000,
    refetchInterval: 30_000,
  });

  // Mapa para lookup rápido por phase_id
  const countsMap = new Map<string, TaskPhaseCount>(
    (query.data || []).map(p => [p.phase_id, p])
  );

  // Invalida contagens
  const invalidateCounts = () => {
    queryClient.invalidateQueries({ queryKey: ['task-kanban-counts', categoryId] });
  };

  return {
    phases: query.data || [],
    countsMap,
    isLoading: query.isLoading,
    invalidateCounts,
  };
}
